import { z } from 'zod';

/** Mirrors the API's error envelope (spec §7). Owned by web, not imported from api. */
export const errorEnvelopeSchema = z.object({
  statusCode: z.number(),
  error: z.string(),
  message: z.array(z.string()),
  correlationId: z.string(),
});

export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

export class ApiError extends Error {
  readonly statusCode: number;
  readonly messages: string[];
  readonly correlationId: string | undefined;

  constructor(statusCode: number, messages: string[], correlationId?: string) {
    super(messages[0] ?? `Request failed with status ${statusCode}`);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.messages = messages;
    this.correlationId = correlationId;
  }

  /** Best-effort parse: an API that returns a non-envelope body still yields a usable error. */
  static async fromResponse(response: Response): Promise<ApiError> {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      return new ApiError(response.status, [response.statusText || 'Request failed']);
    }

    const parsed = errorEnvelopeSchema.safeParse(body);
    return parsed.success
      ? new ApiError(parsed.data.statusCode, parsed.data.message, parsed.data.correlationId)
      : new ApiError(response.status, [response.statusText || 'Request failed']);
  }

  get isUnauthorized(): boolean {
    return this.statusCode === 401;
  }
}
